/**
 *  @flow
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Icon, Header } from 'semantic-ui-react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { Modal, Button } from '../../../../components';

import MembershipForm from '../form/MembershipForm';
import MembershipAppointments from './MembershipAppointments';
import Navigation from './Navigation';

const CloseButton = styled(Button)`
    margin-bottom: 1em !important;
`;

const Subheader = styled(Header.Subheader)`
    font-size: 1rem;
    font-weight: normal;
`;

const ModalActions = styled(Modal.Actions)`
    height: 65px;
`;

function buildInitialValues(membership) {
    return {
        client: membership.client.id,
        service: membership.service.id,
        price: parseFloat(membership.price).toFixed(2),
        totalUses: membership.totalUses,
        quantity: membership.quantity,
        serialNumber: membership.serialNumber,
        createdAt: format(new Date(membership.createdAt), 'yyyy-MM-dd HH:mm'),
    };
}

function MembershipDetails({ trigger, open, onClose, membership }) {
    const [selectedItem, setSelectedItem] = useState('membershipInformation');

    const onItemClick = useCallback((e, { name }) => {
        setSelectedItem(name);
    }, []);

    const methods = useForm({ defaultValues: buildInitialValues(membership) });

    useEffect(() => {
        if (open) {
            methods.reset(buildInitialValues(membership));
        }
    }, [membership.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

    const onModalClose = () => {
        methods.reset();
        onClose();
    };

    return (
        <FormProvider {...methods}>
            <Modal trigger={trigger} onClose={onModalClose} open={open}>
                <Modal.Header>
                    <FormattedMessage
                        id="pages.memberships.form.membership"
                        defaultMessage="Membership"
                    />
                    <Subheader>
                        <FormattedMessage
                            id="pages.memberships.deleteMembership.author"
                            defaultMessage="Created by {firstName} {lastName}"
                            values={{
                                firstName: membership.author.firstName || '',
                                lastName: membership.author.lastName || '',
                            }}
                        />
                    </Subheader>
                </Modal.Header>
                <Navigation selectedItem={selectedItem} onItemClick={onItemClick} />
                <Modal.Content>
                    {selectedItem === 'membershipInformation' ? (
                        <MembershipForm
                            services={[membership.service]}
                            onCloseDialog={onClose}
                        />
                    ) : (
                        <MembershipAppointments membership={membership} />
                    )}
                </Modal.Content>
                <ModalActions>
                    <CloseButton onClick={onModalClose} color="red" floated="right">
                        <Icon name="remove" />
                        <FormattedMessage
                            id="pages.memberships.form.cancel"
                            defaultMessage="Close"
                        />
                    </CloseButton>
                </ModalActions>
            </Modal>
        </FormProvider>
    );
}

export default MembershipDetails;
